/**
 * 조직이 등록한 외부 Dataset.
 *
 * `format` 은 서버가 이 원본을 어떤 포맷으로 판정했는지 말한다. Probe 가능 여부는 서버가
 * `canProbe` 로 따로 준다. 서버는 RLDS · TFDS 를 판정하지만 엔진은 아직 이 포맷을 읽지 못한다.
 * 그래서 FE 는 포맷 이름으로 가능 여부를 다시 추정하지 않는다. 서버 판정(BE `is_samplable`)이 정본이다.
 *
 * `latestProbe` 는 이 Dataset 의 가장 최근 Probe 요약이다. 서버가 요청 시각으로 고른다. Probe 를 요청한 적이
 * 없으면 `null` 이다. 목록이 Probe 를 따로 묻지 않고 상태를 보이게 한다.
 *
 * Owns:
 * - 화면이 다루는 Dataset 값과 노출 · 포맷 판정.
 *
 * Does not own:
 * - wire 응답 해석 — `api/datasetApi.ts`. 표시 문구 — `feature/datasetFormatters.ts`.
 */

import type { ProbeState } from "./probe";

/** 원본을 읽는 방법. 서버 enum 을 그대로 받는다. */
export type DatasetFormat = "lerobot" | "v1" | "rlds" | "tfds" | "misc";

export const DATASET_FORMATS: readonly DatasetFormat[] = ["lerobot", "v1", "rlds", "tfds", "misc"];

/**
 * 원본이 어디에 있는지. 서버 enum 을 그대로 받는다 — 지금 등록할 수 있는 것은
 * `azure_prefix` 뿐이지만(`hf_repo` 는 `400 dataset.source_invalid`), 응답 스키마가 두 값을
 * 선언하므로 목록이 모르는 값에 깨지지 않게 둘 다 안다.
 */
export type DatasetSourceKind = "azure_prefix" | "hf_repo";

export const DATASET_SOURCE_KINDS: readonly DatasetSourceKind[] = ["azure_prefix", "hf_repo"];

/** 지금 등록 요청에 실을 수 있는 유일한 종류. */
export const REGISTERABLE_SOURCE_KIND: DatasetSourceKind = "azure_prefix";

/** 가장 최근 Probe 의 요약. 상세는 Probe 이력이 가진다. */
export type LatestProbe = {
  uuid: string;
  state: ProbeState;
  /** 실패 사유 코드. 실패가 아니면 `null` 이다. */
  failureCode: string | null;
};

export type Dataset = {
  uuid: string;
  sourceKind: DatasetSourceKind;
  /** 정규화된 원본 주소. 서버가 정규화한 값이라 사용자가 입력한 문자열과 다를 수 있다. */
  sourceRef: string;
  displayName: string;
  format: DatasetFormat;
  /** 포맷 안의 버전. 예) `"v2.1"`. `misc` 는 값이 없다. */
  formatVersion: string | null;
  /** 판정 근거 문장. 서버가 한국어로 쓴다. 예) RLDS 를 Probe 할 수 없는 이유. 근거가 없으면 `null`. */
  formatDetail: string | null;
  /** 이 원본으로 Probe 를 받을 수 있는지. 서버가 판정한다. `false` 면 Probe 요청이 `409` 를 받는다. */
  canProbe: boolean;
  /** 숨긴 시각. `null` 이면 목록에 보인다 — 숨김은 삭제가 아니라 가역 상태다. */
  hiddenAtMs: number | null;
  createdAtMs: number;
  /** 가장 최근 Probe. 요청한 적이 없으면 `null` 이다. */
  latestProbe: LatestProbe | null;
};

export function isDatasetHidden(dataset: Dataset): boolean {
  return dataset.hiddenAtMs !== null;
}
